/**
 * Data layer for `conversion-timing-analysis`. Thin typed fetch wrappers over
 * /api/v1/conversion-timing-analysis (FS-303). Raw `fetch()` is confined to this
 * file per the dash contract.
 *
 * Exposes:
 *   - GET /histogram            -> overall latency histogram + percentiles
 *   - GET /breakdown?by=        -> per-dimension histograms
 *   - POST /ingest-pair         -> record one click->conversion pair
 */

export interface HistogramBucket {
	id: string;
	label: string;
	count: number;
}

export interface HistogramResponse {
	total: number;
	buckets: HistogramBucket[];
	median_sec: number | null;
	p90_sec: number | null;
	p99_sec: number | null;
	since_ts: number | null;
	until_ts: number | null;
}

export type Dimension = "campaign" | "affiliate";

export interface BreakdownRow {
	key: string;
	total: number;
	buckets: HistogramBucket[];
	median_sec: number | null;
	p90_sec: number | null;
	p99_sec: number | null;
}

export interface BreakdownResponse {
	dimension: Dimension;
	since_ts: number | null;
	until_ts: number | null;
	breakdown: BreakdownRow[];
}

export interface IngestPairBody {
	click_id: string;
	conversion_id: string;
	campaign_id: string;
	affiliate_id: string;
	click_ts: number;
	conversion_ts: number;
}

export interface IngestPairResponse {
	id: string;
	latency_sec: number;
}

const BASE = "/api/v1/conversion-timing-analysis";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(params: {
	since_ts?: number;
	until_ts?: number;
	by?: Dimension;
}): string {
	const sp = new URLSearchParams();
	if (typeof params.since_ts === "number" && Number.isFinite(params.since_ts)) {
		sp.set("since_ts", String(Math.trunc(params.since_ts)));
	}
	if (typeof params.until_ts === "number" && Number.isFinite(params.until_ts)) {
		sp.set("until_ts", String(Math.trunc(params.until_ts)));
	}
	if (params.by) sp.set("by", params.by);
	const s = sp.toString();
	return s ? `?${s}` : "";
}

export async function fetchHistogram(params: {
	since_ts?: number;
	until_ts?: number;
} = {}): Promise<HistogramResponse> {
	const res = await fetch(`${BASE}/histogram${buildQuery(params)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<HistogramResponse>(res);
}

export async function fetchBreakdown(params: {
	by: Dimension;
	since_ts?: number;
	until_ts?: number;
}): Promise<BreakdownResponse> {
	const res = await fetch(`${BASE}/breakdown${buildQuery(params)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<BreakdownResponse>(res);
}

export async function ingestPair(body: IngestPairBody): Promise<IngestPairResponse> {
	const res = await fetch(`${BASE}/ingest-pair`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<IngestPairResponse>(res);
}

export const conversionTimingAnalysisQueryKeys = {
	all: ["conversion-timing-analysis"] as const,
	histogram: (since?: number, until?: number) =>
		[
			...conversionTimingAnalysisQueryKeys.all,
			"histogram",
			since ?? null,
			until ?? null,
		] as const,
	breakdown: (by: Dimension, since?: number, until?: number) =>
		[
			...conversionTimingAnalysisQueryKeys.all,
			"breakdown",
			by,
			since ?? null,
			until ?? null,
		] as const,
};
